/*
 * Pick row behaviour. Two modes, one file, because both would otherwise fight
 * over the same container.
 *
 *   default    a scroller: cards sit side by side and the row scrolls. Arrows are
 *              a desktop affordance only; the row is already swipeable and
 *              keyboard scrollable without them.
 *   carousel   one card per view, dots, arrows. Opt in per article by setting
 *              card_layout to "carousel" in the gear.picks metafield.
 *
 * BOTH modes drive the SAME native overflow container with scroll snapping. No
 * transforms and no slide index to fall out of sync with the DOM. That is not a
 * shortcut: a transform carousel has to reimplement momentum, swipe, keyboard and
 * snapping, and those reimplementations are where carousels usually break. Here
 * the browser owns all four, and every card stays in the DOM in source order.
 *
 * Carousel mode does take on the one thing scrollers do not need: offscreen cards
 * are inert, so Tab cannot land on a buy button the reader cannot see.
 */
(function () {
  var CAROUSEL = 'gear-awards__grid--carousel';

  function cards(scroller) {
    return Array.prototype.slice.call(scroller.querySelectorAll('.gear-awards__card'));
  }

  function step(scroller) {
    var card = scroller.querySelector('.gear-awards__card');
    if (!card) return scroller.clientWidth;
    var styles = getComputedStyle(scroller);
    var gap = parseFloat(styles.columnGap || styles.gap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  function overflows(scroller) {
    /* 2px of slack: sub-pixel layout can report a hairline overflow no reader can
       actually scroll, and a control that does nothing is worse than none. */
    return scroller.scrollWidth - scroller.clientWidth > 2;
  }

  /* Programmatic movement is animated by hand rather than with
     behavior:'smooth'. Smooth is unreliable here: it silently no-ops in some
     engines and never runs in a backgrounded tab, and measuring it on this very
     page showed a forward step landing while a dot jump and a step back did not
     move at all. Setting scrollLeft directly always works, so the animation is
     driven off rAF where every frame is ours.

     Snapping is suspended for the duration. Mandatory snap fights a per-frame
     scrollLeft, which is what made a multi-card jump stop at the next card. */
  function scrollToLeft(scroller, target) {
    var max = scroller.scrollWidth - scroller.clientWidth;
    target = Math.max(0, Math.min(target, max));

    var start = scroller.scrollLeft;
    var delta = target - start;
    if (Math.abs(delta) < 1) return;

    if (scroller._tween) cancelAnimationFrame(scroller._tween);
    scroller.style.scrollSnapType = 'none';

    var t0 = null;
    var DURATION = 320;

    function settle() {
      if (scroller._tween) cancelAnimationFrame(scroller._tween);
      if (scroller._guard) clearTimeout(scroller._guard);
      scroller._tween = scroller._guard = null;
      scroller.scrollLeft = target;
      scroller.style.scrollSnapType = '';
      /* Update the dots and the inert state now rather than waiting on the scroll
         listener, which is rAF-gated and therefore late in a throttled tab. */
      if (typeof scroller._sync === 'function') scroller._sync();
    }

    function frame(ts) {
      if (t0 === null) t0 = ts;
      var p = Math.min(1, (ts - t0) / DURATION);
      /* easeInOutQuad */
      var e = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
      scroller.scrollLeft = start + delta * e;
      if (p < 1) scroller._tween = requestAnimationFrame(frame);
      else settle();
    }

    /* rAF is throttled to a crawl in a backgrounded or headless tab, which leaves
       the tween stalled part-way and snapping switched off. The timer guarantees
       the final position and the cleanup no matter how few frames actually ran. */
    if (scroller._guard) clearTimeout(scroller._guard);
    scroller._guard = setTimeout(settle, DURATION + 150);
    scroller._tween = requestAnimationFrame(frame);
  }

  function activeIndex(scroller) {
    var s = step(scroller);
    if (!s) return 0;
    return Math.round(scroller.scrollLeft / s);
  }

  function button(dir, label, hideFromA11y) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'gear-scroll__btn gear-scroll__btn--' + dir;
    b.setAttribute('aria-label', label);
    if (hideFromA11y) {
      /* Scroller mode: the row itself is a labelled, keyboard-scrollable region,
         so these are a pointer shortcut rather than the only route through. */
      b.setAttribute('aria-hidden', 'true');
      b.tabIndex = -1;
    }
    b.innerHTML = dir === 'prev'
      ? '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M15 4 7 12l8 8" fill="none" stroke="currentColor" stroke-width="2"/></svg>'
      : '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="m9 4 8 8-8 8" fill="none" stroke="currentColor" stroke-width="2"/></svg>';
    return b;
  }

  /* Keep Tab out of cards the reader cannot see. inert where supported, and a
     tabindex sweep where it is not, so no browser leaves a hidden buy button
     focusable. */
  function setInert(card, off) {
    if ('inert' in HTMLElement.prototype) {
      card.inert = off;
    }
    card.setAttribute('aria-hidden', off ? 'true' : 'false');
    card.querySelectorAll('a, button, input').forEach(function (el) {
      if (off) {
        if (!el.hasAttribute('data-prev-tabindex')) {
          el.setAttribute('data-prev-tabindex', el.getAttribute('tabindex') || '');
        }
        el.setAttribute('tabindex', '-1');
      } else {
        var prev = el.getAttribute('data-prev-tabindex');
        if (prev === '') el.removeAttribute('tabindex');
        else if (prev !== null) el.setAttribute('tabindex', prev);
        el.removeAttribute('data-prev-tabindex');
      }
    });
  }

  function enhance(scroller) {
    if (scroller.dataset.scrollEnhanced) return;
    scroller.dataset.scrollEnhanced = '1';

    var isCarousel = scroller.classList.contains(CAROUSEL);
    var items = cards(scroller);

    var wrap = document.createElement('div');
    wrap.className = 'gear-scroll' + (isCarousel ? ' gear-scroll--carousel' : '');
    scroller.parentNode.insertBefore(wrap, scroller);
    wrap.appendChild(scroller);

    var prev = button('prev', isCarousel ? 'Previous pick' : 'Scroll to previous picks', !isCarousel);
    var next = button('next', isCarousel ? 'Next pick' : 'Scroll to next picks', !isCarousel);
    wrap.appendChild(prev);
    wrap.appendChild(next);

    var dots = null;
    if (isCarousel && items.length > 1) {
      dots = document.createElement('div');
      dots.className = 'gear-dots';
      dots.setAttribute('role', 'tablist');
      dots.setAttribute('aria-label', 'Choose a pick');
      items.forEach(function (card, i) {
        var d = document.createElement('button');
        d.type = 'button';
        d.className = 'gear-dots__dot';
        d.setAttribute('role', 'tab');
        var name = card.querySelector('.gear-awards__name');
        d.setAttribute('aria-label', name ? name.textContent.trim() : 'Pick ' + (i + 1));
        d.addEventListener('click', function () {
          scrollToLeft(scroller, i * step(scroller));
        });
        dots.appendChild(d);
      });
      wrap.appendChild(dots);
    }

    function sync() {
      if (!overflows(scroller)) {
        prev.hidden = next.hidden = true;
        if (dots) dots.hidden = true;
        items.forEach(function (c) { setInert(c, false); });
        return;
      }
      prev.hidden = next.hidden = false;
      if (dots) dots.hidden = false;

      var max = scroller.scrollWidth - scroller.clientWidth;
      prev.disabled = scroller.scrollLeft <= 2;
      next.disabled = scroller.scrollLeft >= max - 2;

      if (!isCarousel) return;

      var active = activeIndex(scroller);
      if (dots) {
        Array.prototype.forEach.call(dots.children, function (d, i) {
          d.classList.toggle('is-active', i === active);
          d.setAttribute('aria-selected', i === active ? 'true' : 'false');
        });
      }
      items.forEach(function (c, i) { setInert(c, i !== active); });
    }

    prev.addEventListener('click', function () {
      scrollToLeft(scroller, scroller.scrollLeft - step(scroller));
    });
    next.addEventListener('click', function () {
      scrollToLeft(scroller, scroller.scrollLeft + step(scroller));
    });

    scroller._sync = sync;

    var queued = false;
    function onScroll() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; sync(); });
    }
    scroller.addEventListener('scroll', onScroll, { passive: true });

    if (window.ResizeObserver) new ResizeObserver(sync).observe(scroller);
    else window.addEventListener('resize', onScroll);

    /* Card images load lazily, so the row's width is not final at DOMContentLoaded. */
    window.addEventListener('load', sync);
    sync();
  }

  function init() {
    document.querySelectorAll('.gear-awards__grid').forEach(enhance);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
